import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import './NotificationsLaunches.scss';

import { updateNotificationRollout, updateNotificationLaunchPlan, searchNotificationRecipients, retireNotificationRegistryEntry } from '../../api/notifications';
import { pluralizeCount } from '../../helpers/formatting';
import {
  humanizeSentFrom,
  modeChangeConfirm,
  modeChangeToast,
  retireConfirmFor,
  retireToast,
  holdoutChangeConfirm,
  holdoutChangeToast,
  launchMovesFor,
  UNSET_MODE_NOTE,
  SENT_FROM_CHOICES,
  sentFromChoiceOf,
  TIPS as REGISTRY_TIPS
} from '../../helpers/notification_registry_helpers';
import InfoTip from './elements/InfoTip';
import LaunchPlanField from './elements/LaunchPlanField';
import NotificationMoveButton from './elements/NotificationMoveButton';
import HeaderToolbar from './elements/HeaderToolbar';
import EntityAvatar from './elements/EntityAvatar';
import UserSearchPicker from '../General/UserSearchPicker';
import { confirmAlert } from 'react-confirm-alert';
import { confirmRetire } from './elements/confirmRetire';

const CHANNEL_SHORT = { push: 'push', email: 'email', center: 'Center' };
const STAGE_LABELS = { shadow: 'Shadow', allowlist: 'Allowlist', live: 'Live', paused: 'Paused' };

const GROUPS = [
  {
    id: 'ready',
    title: 'Ready to move',
    why: 'The gate passes, including any Shadow period. Nothing but a decision stands between these and the next rung.'
  },
  {
    id: 'allow',
    title: 'On Allowlist',
    why: 'Sending to the listed accounts, and recording a shadow receipt for everyone else.',
    empty: 'Nothing is on Allowlist.'
  },
  {
    id: 'holdout',
    title: 'Live with a holdout',
    why: 'Sending, except the holdout group misses the push and the email. Set the holdout to No and the lane leaves Launches.',
    hideWhenEmpty: true
  },
  {
    id: 'waiting',
    title: 'Waiting on someone',
    why: 'The gate fails on a task. The row names the first failing check. A lane that fails more than one still matches any of those tasks.'
  },
  {
    id: 'silent',
    title: 'Silent in Shadow',
    why: 'Has a Shadow period and no receipt in 7 days, so there is nothing to read.'
  },
  {
    id: 'period',
    title: 'In Shadow period',
    why: 'Every other check passes. The lane is waiting out its Shadow period.'
  },
  {
    id: 'paused',
    title: 'Paused',
    why: 'It was sending and is stopped. Resume Live sends it again.',
    hideWhenEmpty: true
  }
];

const TIPS = {
  stage:
    'Shadow: records what it would send and sends nothing. Allowlist: sends to the people on its list, and records a shadow receipt for everyone else. Live with holdout: sending, except the holdout group misses the push and the email. Paused: it was sending, and moving it back to Shadow stopped it.',
  waiting:
    'Copy: the push, email, or Notification Center words are not approved. Tap: nobody has said where a tap opens. Switch: it has no mute switch, or the switch is not in settings yet. Flow: it is a Klaviyo flow and the flow link is missing. Sent from: nobody has said which system sends it.',
  sent: 'Shadow receipts this lane would have sent over the last 7 days. A Live lane shows what it sent.',
  gate: 'Each check is pass or fail. The first one that fails is named on the row. The count is how many checks pass.',
  plan: 'Shadow period and holdout for this notification. Both default to No.',
  owner: 'The person named on the Registry entry.',
  mode:
    'The moves this notification can make from where it sits. Each one asks before it saves. Shadow records and sends nothing. Allowlist sends to the people on its list and records a shadow receipt for everyone else. Live sends. Pause stops a Live one; Resume Live starts it again.'
};

// A Live row is only on the tab while its holdout is on.
const STAGE_FILTERS = [
  { value: 'all', label: 'All', matches: () => true },
  { value: 'shadow', label: 'Shadow', matches: row => row.stage === 'shadow' },
  { value: 'allowlist', label: 'Allowlist', matches: row => row.stage === 'allowlist' },
  { value: 'live', label: 'Live with holdout', matches: row => row.stage === 'live' }
];
const WAITING_FILTERS = [
  { value: 'Anyone', label: 'Anyone', matches: () => true },
  ...['Copy', 'Tap', 'Switch', 'Flow', 'Sent from'].map(task => ({ value: task, label: task, matches: row => (row.tasks || []).includes(task) }))
];
const SENT_FILTERS = [
  { value: 'all', label: 'All', matches: () => true },
  ...[...SENT_FROM_CHOICES, { value: '', label: 'Not decided' }].map(({ value, label }) => ({
    value: value || 'undecided',
    label,
    matches: row => sentFromChoiceOf({ sentFrom: row.sentFrom, pushDelivery: row.pushDelivery }) === value
  }))
];

const filterFor = (filters, value) => _.find(filters, { value });

const searchAllowlistUsers = query => searchNotificationRecipients(query).then(data => data.users || []);
const VISIBLE_ALLOWLIST_FACES = 3;

const allowlistName = user => user.name || user.username || `User ${user.id}`;

const allowlistInitials = user => {
  const parts = (user.name || user.username || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  const first = Array.from(parts[0])[0];
  if (parts.length === 1) return first.toUpperCase();
  return `${first}${Array.from(parts[parts.length - 1])[0]}`.toUpperCase();
};

const allowlistHandle = user => [user.username && `@${user.username}`, `#${user.id}`].filter(Boolean).join(' · ');

// An undecided Sent from is the gate's to say, so the name line carries only what is known.
const fromLine = row => {
  const channels = (row.channels || []).map(channel => CHANNEL_SHORT[channel] || channel).join(', ');
  return [row.sentFrom && humanizeSentFrom(row.sentFrom, { pushDelivery: row.pushDelivery }), channels].filter(Boolean).join(' · ');
};

const planNote = plan => {
  if (plan.shadowPeriodSource === 'plan' || plan.holdoutSource === 'plan') return 'Set on this notification';
  if (plan.holdoutSource === 'launch_row') return 'Kept from when it launched';
  return 'Default';
};

const volumeCount = row => (row.stage === 'live' ? row.week?.sent : row.week?.shadow) || 0;

const applyChange = (promise, fallback) =>
  promise.catch(err => {
    window.ALERT?.error?.(err?.error || fallback);
    return null;
  });

const modeChangeStateFor = (row, mode) => ({
  name: row.name,
  mode,
  fromMode: row.mode,
  status: row.status,
  holdoutPercent: row.plan.holdoutPercent,
  allowlistCount: (row.allowlistUsers || []).length
});

// Every built notification that is not fully live, grouped by what has to happen before it leaves.
const NotificationsLaunches = ({
  rows,
  planned = 0,
  loading = false,
  onReload,
  canChangeRollout = false,
  onOpenEntry,
  onOpenPlanned,
  toolbarSlot = null
}) => {
  const [stage, setStage] = useState('all');
  const [waiting, setWaiting] = useState('Anyone');
  const [sentFrom, setSentFrom] = useState('all');
  const [query, setQuery] = useState('');
  const [openType, setOpenType] = useState(null);
  const [allowlistType, setAllowlistType] = useState(null);
  const closeAllowlist = type => setAllowlistType(current => (current === type ? null : current));

  if (loading) return <div className='notifications-launches-outer'>Loading…</div>;
  if (!rows) return <div className='notifications-launches-outer'>Could not load Launches.</div>;

  const q = query.trim().toLowerCase();
  const matchesQuery = row => !q || [row.name, row.type].some(text => (text || '').toLowerCase().includes(q));
  const facetOn = stage !== 'all' || waiting !== 'Anyone' || sentFrom !== 'all';
  const filtering = facetOn || !!q;
  const activeRows = rows.filter(row => row.group !== 'paused');
  const shown = activeRows.filter(
    row =>
      filterFor(STAGE_FILTERS, stage).matches(row) &&
      filterFor(WAITING_FILTERS, waiting).matches(row) &&
      filterFor(SENT_FILTERS, sentFrom).matches(row) &&
      matchesQuery(row)
  );
  const shownPaused = facetOn ? [] : rows.filter(row => row.group === 'paused' && matchesQuery(row));
  const counts = _.countBy(rows, 'group');
  const stale = rows.filter(row => row.stale).length;
  const summary = [
    ['plain', counts.ready || 0, 'ready to move'],
    ...(stale ? [['alert', stale, 'ready for more than 7 days']] : []),
    ['plain', counts.allow || 0, 'on Allowlist'],
    ['plain', counts.holdout || 0, 'Live with a holdout'],
    ['plain', counts.waiting || 0, 'waiting on someone'],
    ['plain', counts.silent || 0, 'silent in Shadow'],
    ['plain', counts.period || 0, 'in their Shadow period'],
    ['plain', rows.filter(row => row.stage === 'shadow').length, 'in Shadow'],
    ['link', planned, 'planned in Registry'],
    ...(counts.paused ? [['plain', counts.paused, 'paused']] : [])
  ];

  const saveMode = (row, mode) =>
    applyChange(updateNotificationRollout(row.type, { mode }), 'Could not change the mode.').then(resp => {
      if (!resp) return;
      if (mode !== 'allowlist') closeAllowlist(row.type);
      window.ALERT?.success?.(modeChangeToast(modeChangeStateFor(row, mode)));
      onReload();
    });
  const retire = row => {
    const ask = retireConfirmFor({
      name: row.name,
      stage: row.stage,
      allowlistCount: (row.allowlistUsers || []).length,
      sentFrom: row.sentFrom,
      hasTemplate: !!row.hasTemplate,
      holdoutPercent: row.plan?.holdoutPercent || 0
    });
    confirmRetire({
      ...ask,
      onRetire: reason =>
        applyChange(retireNotificationRegistryEntry(row.type, reason), 'Could not retire it.').then(resp => {
          if (!resp) return;
          window.ALERT?.success?.(retireToast({ name: row.name, sentFrom: row.sentFrom, hasTemplate: !!row.hasTemplate }));
          onReload();
        })
    });
  };
  const saveAllowlist = (row, users) =>
    applyChange(
      updateNotificationRollout(row.type, { mode: 'allowlist', allowlistUserIds: users.map(user => user.id) }),
      'Could not update the allowlist.'
    ).then(resp => {
      if (resp) onReload();
      return resp;
    });
  const saveHoldout = (row, holdoutPercent) =>
    applyChange(updateNotificationLaunchPlan(row.type, { holdoutPercent }), 'Could not change the holdout.').then(resp => {
      if (!resp) return;
      window.ALERT?.success?.(holdoutChangeToast({ name: row.name, live: row.mode === 'live', percent: resp.launchPlan?.holdoutPercent }));
      onReload();
    });

  const changeMode = (row, mode) => {
    const dialog = modeChangeConfirm(modeChangeStateFor(row, mode));
    confirmAlert({
      title: dialog.title,
      message: dialog.message,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: dialog.confirm, onClick: () => saveMode(row, mode) }
      ]
    });
  };
  const changeHoldout = (row, holdoutPercent) => {
    const dialog = holdoutChangeConfirm({ name: row.name, live: row.mode === 'live', from: row.plan.holdoutPercent, to: holdoutPercent });
    if (!dialog) return saveHoldout(row, holdoutPercent);
    confirmAlert({
      title: dialog.title,
      message: dialog.message,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: dialog.confirm, onClick: () => saveHoldout(row, holdoutPercent) }
      ]
    });
  };

  return (
    <div className='notifications-launches-outer'>
      <HeaderToolbar slot={toolbarSlot} className='launches-toolbar'>
        <div className='summary'>
          {summary.map((item, index) => (
            <React.Fragment key={item[2]}>
              {index > 0 && <span className='sep'>·</span>}
              {item[0] === 'link' ? (
                <button type='button' className='summary-link' onClick={onOpenPlanned}>
                  {item[1]} {item[2]}
                </button>
              ) : (
                <span className={item[0] === 'alert' ? 'alert' : undefined}>
                  <b>{item[1]}</b> {item[2]}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
        <input className='launch-filter' value={query} placeholder='Search name or short code' onChange={event => setQuery(event.target.value)} />
        <div className='facets'>
          <Facet label='Stage' tip={TIPS.stage} options={STAGE_FILTERS} rows={activeRows} value={stage} onChange={setStage} />
          <Facet label='Waiting on' tip={TIPS.waiting} options={WAITING_FILTERS} rows={activeRows} value={waiting} onChange={setWaiting} />
          <Facet label='Sent from' options={SENT_FILTERS} rows={activeRows} value={sentFrom} onChange={setSentFrom} />
        </div>
      </HeaderToolbar>
      {!rows.length && <div className='launch-empty'>Every built notification is Live, with no holdout left on.</div>}
      {!!rows.length && filtering && !shown.length && !shownPaused.length && <div className='launch-empty'>No notifications match.</div>}
      {GROUPS.map(group => {
        const groupRows = group.id === 'paused' ? shownPaused : shown.filter(row => row.group === group.id);
        if (!groupRows.length && (group.hideWhenEmpty || filtering || !group.empty)) return null;
        return (
          <section key={group.id} className='launch-card'>
            <div className='card-head'>
              <span className='card-title'>{group.title}</span>
              <span className='card-note'>{group.why}</span>
            </div>
            {!groupRows.length ? (
              <div className='launch-empty'>{group.empty}</div>
            ) : (
              <div className='launch-table'>
                <div className='launch-row head'>
                  <span>Notification</span>
                  <span>
                    Stage <InfoTip message={TIPS.stage} />
                  </span>
                  <span className='stack-head'>
                    <span>Would have sent</span>
                    <span className='stack-sub'>
                      7 days <InfoTip message={TIPS.sent} />
                    </span>
                  </span>
                  <span>
                    Gate <InfoTip message={TIPS.gate} />
                  </span>
                  <span>
                    Plan <InfoTip message={TIPS.plan} />
                  </span>
                  <span>
                    Owner <InfoTip message={TIPS.owner} />
                  </span>
                  <span>
                    Action <InfoTip message={TIPS.mode} />
                  </span>
                </div>
                {groupRows.map(row => (
                  <LaunchRow
                    key={row.type}
                    row={row}
                    open={openType === row.type}
                    onToggle={() => setOpenType(openType === row.type ? null : row.type)}
                    allowlistOpen={row.mode === 'allowlist' && allowlistType === row.type}
                    onToggleAllowlist={() => setAllowlistType(current => (current === row.type ? null : row.type))}
                    onCloseAllowlist={closeAllowlist}
                    onOpenEntry={() => onOpenEntry(row.type)}
                    canChangeRollout={canChangeRollout}
                    onChangeMode={changeMode}
                    onRetire={retire}
                    onChangeAllowlist={saveAllowlist}
                    onChangeHoldout={percent => changeHoldout(row, percent)}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

const Facet = ({ label, tip, options, rows, value, onChange }) => (
  <div className='facet'>
    <div className='facet-label'>
      {label}
      {tip && <InfoTip message={tip} />}
    </div>
    <div className='chips'>
      {options.map(option => (
        <button key={option.value} type='button' className={cn('chip', { on: value === option.value })} onClick={() => onChange(option.value)}>
          {option.label}
          <span className='count'>{rows.filter(option.matches).length}</span>
        </button>
      ))}
    </div>
  </div>
);

const LaunchAllowlist = ({ users, canEdit, open, onToggle, onChange }) => {
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      const target = event.target;
      if (rootRef.current?.contains(target) || target.closest?.('.user-search-picker-dropdown')) return;
      onToggle();
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open, onToggle]);
  const countLabel = `${users.length} on the list`;
  if (!canEdit) return <span className='mode-extra'>{countLabel}</span>;
  const faces = users.slice(0, VISIBLE_ALLOWLIST_FACES);
  return (
    <div className={cn('launch-allowlist', { open })} ref={rootRef}>
      <button type='button' className={cn('allowlist-count', { open })} aria-expanded={open} onClick={onToggle}>
        {!!faces.length && (
          <span className='allowlist-faces'>
            {faces.map(user => (
              <span key={user.id}>{allowlistInitials(user)}</span>
            ))}
          </span>
        )}
        {countLabel}
      </button>
      {open && (
        <div className='allowlist-panel'>
          <div className='allowlist-head'>
            <strong>Allowlist</strong>
            <span>{pluralizeCount(users.length, 'person', 'people')}</span>
          </div>
          <UserSearchPicker
            selectedUsers={users}
            onChange={onChange}
            searchUsers={searchAllowlistUsers}
            placeholder='Search by name, username, email, or user id'
            showUserIds
            hideSelectedList
            autoFocus
          />
          <ul className='allowlist-people'>
            {users.map(user => (
              <li key={user.id}>
                <div className='who'>
                  <b>{allowlistName(user)}</b>
                  <em>{allowlistHandle(user)}</em>
                </div>
                <button
                  type='button'
                  aria-label={`Remove ${allowlistName(user)}`}
                  onClick={() => onChange(users.filter(selected => selected.id !== user.id))}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const LaunchRow = ({
  row,
  open,
  onToggle,
  allowlistOpen,
  onToggleAllowlist,
  onCloseAllowlist,
  onOpenEntry,
  canChangeRollout,
  onChangeMode,
  onRetire,
  onChangeAllowlist,
  onChangeHoldout
}) => {
  const count = volumeCount(row);
  const allowlistOpenRef = useRef(allowlistOpen);
  const closeAllowlistRef = useRef(onCloseAllowlist);
  allowlistOpenRef.current = allowlistOpen;
  closeAllowlistRef.current = onCloseAllowlist;
  useEffect(
    () => () => {
      if (allowlistOpenRef.current) closeAllowlistRef.current(row.type);
    },
    [row.type]
  );
  const days = row.daysInStage == null ? null : `${row.daysInStage} ${row.daysInStage === 1 ? 'day' : 'days'}`;
  const gateText = row.total ? `${row.passedCount} of ${row.total} checks` : null;
  const canMove = row.hasTemplate && (!row.sentFrom || row.sentFrom === 'backend');
  const moves = launchMovesFor({ stage: row.stage, mode: row.mode, gatePassed: row.passed, lockedReason: row.failingDetail });
  // The picker builds each change from the list it is shown, so the list follows the edits until the
  // row reloads with the saved one.
  const [allowlistDraft, setAllowlistDraft] = useState(null);
  useEffect(() => setAllowlistDraft(null), [row]);
  const changeAllowlist = users => {
    setAllowlistDraft(users);
    onChangeAllowlist(row, users).then(resp => !resp && setAllowlistDraft(null));
  };
  return (
    <>
      <div className='launch-row' onClick={onToggle}>
        <button
          type='button'
          className='entry-name'
          onClick={event => {
            event.stopPropagation();
            onOpenEntry();
          }}
        >
          <span className='name'>{row.name}</span>
          <span className='code'>{row.type}</span>
          <span className='from'>{fromLine(row)}</span>
        </button>
        <div className='stage-cell'>
          <span className={cn('badge', `stage-${row.stage}`)}>{STAGE_LABELS[row.stage] || row.stage}</span>
          {days && <span className={cn('days', { stale: row.stale })}>{days}</span>}
          {row.stale && <div className='stale-line'>Ready for more than 7 days</div>}
        </div>
        <div className={cn('volume', { alert: row.group === 'silent' && !count, quiet: row.group !== 'silent' && !count })}>
          {count.toLocaleString('en-US')}
        </div>
        <div className={row.passed ? 'gate-ok' : 'gate-fail'}>
          <span className={cn('mark', row.passed ? 'ok' : 'bad')}>{row.passed ? '✓' : '✕'}</span>
          <div>
            {!row.passed && <div className='what'>{row.failingKey === 'shadow_period' ? row.failingDetail : row.failingLabel}</div>}
            {gateText && <div className='count'>{gateText}</div>}
          </div>
        </div>
        <div className='plan-cell' onClick={event => event.stopPropagation()}>
          {row.group === 'holdout' ? (
            <>
              <div className='plan-line'>
                Holdout group <InfoTip message={REGISTRY_TIPS.holdout} />
              </div>
              <LaunchPlanField
                value={row.plan.holdoutPercent}
                source={row.plan.holdoutSource}
                unit='%'
                max={50}
                yesValue={10}
                canEdit={canChangeRollout}
                describe={percent =>
                  percent
                    ? `Once Live, ${percent}% of creators never get the push or the email. They still get the Notification Center row. Setting it to No sends it to everyone, and the lane leaves Launches.`
                    : 'Once Live, every creator it fires for gets the push and the email.'
                }
                onSave={onChangeHoldout}
              />
            </>
          ) : (
            <>
              <div className={cn('plan-line', { muted: !row.plan.shadowPeriodDays })}>
                {row.plan.shadowPeriodDays ? `Shadow period · ${row.plan.shadowPeriodDays} days` : 'No Shadow period'}
              </div>
              <div className={cn('plan-line', { muted: !row.plan.holdoutPercent })}>
                {row.plan.holdoutPercent ? `Holdout · ${row.plan.holdoutPercent}%` : 'No holdout'}
              </div>
              <div className='plan-note'>{planNote(row.plan)}</div>
            </>
          )}
        </div>
        <div className='owner'>
          {row.owner ? (
            <>
              <EntityAvatar image={row.owner.image} name={row.owner.name} className='owner-avatar' />
              <span>{row.owner.name}</span>
            </>
          ) : (
            <span className='no-owner'>No owner</span>
          )}
        </div>
        <div className='mode-cell' onClick={event => event.stopPropagation()}>
          {canMove ? (
            <>
              <div className='mode-control'>
                {moves.map(move => (
                  <NotificationMoveButton key={move.mode} move={move} enabled={canChangeRollout} onClick={() => onChangeMode(row, move.mode)} />
                ))}
              </div>
              {!row.onLadder && <span className='mode-extra'>{UNSET_MODE_NOTE}</span>}
              {row.mode === 'allowlist' && (
                <LaunchAllowlist
                  users={allowlistDraft || row.allowlistUsers || []}
                  canEdit={canChangeRollout}
                  open={allowlistOpen}
                  onToggle={onToggleAllowlist}
                  onChange={changeAllowlist}
                />
              )}
            </>
          ) : (
            <span className='mode-extra'>Stage follows the Registry.</span>
          )}
          {canChangeRollout && (
            <button type='button' className='retire-link' onClick={() => onRetire(row)}>
              Retire
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className='launch-drawer'>
          {row.checks.map(check => (
            <div key={check.key} className='check'>
              <span className={cn('mark', check.passed ? 'ok' : 'bad')}>{check.passed ? '✓' : '✕'}</span>
              <span>
                {check.label}
                {check.detail ? <span className='detail'>{check.detail}</span> : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

NotificationsLaunches.propTypes = {
  rows: PropTypes.array,
  planned: PropTypes.number,
  loading: PropTypes.bool,
  onReload: PropTypes.func.isRequired,
  canChangeRollout: PropTypes.bool,
  onOpenEntry: PropTypes.func.isRequired,
  onOpenPlanned: PropTypes.func.isRequired,
  toolbarSlot: PropTypes.instanceOf(Element)
};

Facet.propTypes = {
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  options: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

LaunchAllowlist.propTypes = {
  users: PropTypes.array.isRequired,
  canEdit: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

LaunchRow.propTypes = {
  row: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  allowlistOpen: PropTypes.bool.isRequired,
  onToggleAllowlist: PropTypes.func.isRequired,
  onCloseAllowlist: PropTypes.func.isRequired,
  onOpenEntry: PropTypes.func.isRequired,
  canChangeRollout: PropTypes.bool,
  onChangeMode: PropTypes.func.isRequired,
  onRetire: PropTypes.func.isRequired,
  onChangeAllowlist: PropTypes.func.isRequired,
  onChangeHoldout: PropTypes.func.isRequired
};

export default NotificationsLaunches;
